import { lazy } from 'react';

/**
 * Drop-in replacement for React.lazy that survives a deploy.
 *
 * Vite emits content-hashed chunk names, so a new build produces new filenames
 * and deletes the old ones. A tab still running the previous build asks for a
 * chunk that no longer exists, the dynamic import rejects with 404, and
 * react-router would show "Failed to fetch dynamically imported module".
 *
 * Recovery is to reload: the browser then fetches the fresh index.html (served
 * no-cache) and the chunk names it references, landing the user on the page
 * they actually asked for.
 *
 * If the chunk is still missing after that reload, the error is rethrown and
 * the router's errorElement (RouteErrorFallback) takes over and sends the user
 * to the dashboard. Either way react-router's raw error screen is never shown.
 */

const ATTEMPT_KEY = 'chunkReloadAt';

/**
 * Reloads are rate limited by TIME rather than "once per session".
 *
 * A counter cleared on the next successful chunk load would re-arm mid-bounce:
 * if a chunk is genuinely missing from the current build, the errorElement
 * redirect loads other chunks successfully, which would clear the flag and
 * allow another reload — a loop. A timestamp cannot be re-armed that way.
 */
const RELOAD_COOLDOWN_MS = 15000;

export const shouldAttemptReload = (now = Date.now()) => {
  try {
    const last = Number(sessionStorage.getItem(ATTEMPT_KEY)) || 0;
    if (now - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(ATTEMPT_KEY, String(now));
    return true;
  } catch {
    // sessionStorage can throw (private mode). Without somewhere to record the
    // attempt we cannot rule out a loop, so do not reload.
    return false;
  }
};

/** True for "the chunk file is missing/unreachable", not "the module threw". */
export const isChunkLoadError = (error) => {
  const message = String(error?.message || error || '');
  return (
    /dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /ChunkLoadError/i.test(String(error?.name || ''))
  );
};

const lazyWithReload = (factory) =>
  lazy(() =>
    factory().catch((error) => {
      if (isChunkLoadError(error) && shouldAttemptReload()) {
        window.location.reload();
        // Deliberately never settles: the reload replaces this document.
        return new Promise(() => {});
      }
      throw error;
    }),
  );

export default lazyWithReload;
